import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { asString } from "@/components/shapes/shared/plain-record";
import { cn } from "@/lib/cn";
import { fileSizeLabel } from "@/lib/file-size-label";
import { FILE_CARD_RATIO, fileNameParts } from "./file-card-shape";

/**
 * A file other than a PDF, the card and the top of its opened window: a
 * calm card with its type chip, its name and its type and size (an
 * upload's `sizeBytes` in the user's language, else the stored `size`),
 * and where a linked file is when it is not here (`note`).
 */
export function FileFace({
  source,
  density,
  note = null,
  testIdPrefix,
}: {
  source: Record<string, unknown>;
  density: CardPreviewDensity;
  /** "On <computer>" or "File not found" (`useFileNote`). */
  note?: string | null;
  /** `file-card` or `file-frame-face`: its test id and its parts' prefix. */
  testIdPrefix: string;
}) {
  const { stem, label } = fileNameParts(source);
  const size = fileSizeLabel(source.sizeBytes) || asString(source.size);
  const compact = density === "compact";
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col justify-between overflow-hidden p-[1.4em]",
        compact && "h-full",
      )}
      style={compact ? undefined : { aspectRatio: FILE_CARD_RATIO }}
      data-testid={testIdPrefix}
    >
      <span
        aria-hidden="true"
        className="grid h-[3.2em] w-[2.6em] place-items-end justify-center rounded-[0.35em_0.9em_0.35em_0.35em] bg-card-line pb-[0.45em] text-[0.78em] font-extrabold tracking-[0.04em] text-card-muted"
        data-testid={`${testIdPrefix}-chip`}
      >
        {label}
      </span>
      <span className="min-w-0">
        <span
          className="line-clamp-3 font-display text-[1.45em] font-medium leading-[1.15] tracking-[-0.02em] text-card-ink"
          data-testid={`${testIdPrefix}-name`}
        >
          {stem || label}
        </span>
        <span
          className="mt-[0.6em] block truncate text-[0.8em] text-card-muted"
          data-testid={`${testIdPrefix}-meta`}
        >
          {[label, size, note].filter(Boolean).join(" · ")}
        </span>
      </span>
    </div>
  );
}
